export interface IncomingMessageSound {
	messageId: string;
	chatId: string;
}

type Listener = (message: IncomingMessageSound) => void;

const DEDUPLICATION_WINDOW_MS = 2 * 60 * 1000;
const MIN_TONE_INTERVAL_MS = 1000;

class MessageSound {
	private context: AudioContext | null = null;
	private listeners = new Set<Listener>();
	private seen = new Map<string, number>();
	private lastToneAt = 0;

	async unlock(): Promise<void> {
		if (typeof window === 'undefined' || !('AudioContext' in window)) return;
		this.context ??= new AudioContext();
		try {
			await this.context.resume();
			const gain = this.context.createGain();
			gain.gain.value = 0.00001;
			gain.connect(this.context.destination);
			const oscillator = this.context.createOscillator();
			oscillator.connect(gain);
			oscillator.start();
			oscillator.stop(this.context.currentTime + 0.01);
		} catch {
			// Следующая пользовательская активация повторит разблокировку Web Audio.
		}
	}

	emit(message: IncomingMessageSound): void {
		const now = Date.now();
		for (const [messageId, seenAt] of this.seen) {
			if (now - seenAt > DEDUPLICATION_WINDOW_MS) this.seen.delete(messageId);
		}
		if (this.seen.has(message.messageId)) return;
		this.seen.set(message.messageId, now);
		for (const listener of this.listeners) listener(message);
	}

	subscribe(listener: Listener): () => void {
		this.listeners.add(listener);
		return () => this.listeners.delete(listener);
	}

	play(): boolean {
		const context = this.context;
		const now = Date.now();
		if (!context || context.state !== 'running' || now - this.lastToneAt < MIN_TONE_INTERVAL_MS) {
			return false;
		}
		this.lastToneAt = now;
		this.tone(context, 0, 0.12, 940);
		this.tone(context, 0.13, 0.1, 760);
		return true;
	}

	private tone(context: AudioContext, offset: number, duration: number, frequency: number): void {
		const oscillator = context.createOscillator();
		const gain = context.createGain();
		const start = context.currentTime + offset;
		const end = start + duration;
		oscillator.type = 'sine';
		oscillator.frequency.value = frequency;
		gain.gain.setValueAtTime(0.0001, start);
		gain.gain.exponentialRampToValueAtTime(0.1, start + 0.015);
		gain.gain.exponentialRampToValueAtTime(0.0001, end);
		oscillator.connect(gain);
		gain.connect(context.destination);
		oscillator.start(start);
		oscillator.stop(end + 0.02);
	}
}

export const messageSound = new MessageSound();
